import { fromZonedTime } from 'date-fns-tz';
import { toZonedTime } from 'date-fns-tz/toZonedTime';
import { endOfWeek } from 'date-fns/endOfWeek';
import { format } from 'date-fns/format';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfWeek } from 'date-fns/startOfWeek';

const australiaPerthTz = 'Australia/Perth';

/** Parse API TimeSpan ("HH:mm:ss" / "HH:mm") into hours+minutes. */
export function parseOrderingTimeOfDay(
  value?: string | null,
): { hours: number; minutes: number } | null {
  if (!value) return null;
  const match = value.trim().match(/(?:(\d+)\.)?(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!match) return null;
  const hours = Number(match[2]);
  const minutes = Number(match[3]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return { hours, minutes };
}

/** Convert API time to HTML time input value ("HH:mm"). */
export function orderingTimeToInputValue(value?: string | null): string {
  const parsed = parseOrderingTimeOfDay(value);
  if (!parsed) return '';
  return `${String(parsed.hours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')}`;
}

/** Convert HTML time input ("HH:mm") to API TimeSpan ("HH:mm:ss"). */
export function orderingTimeFromInputValue(value: string): string | null {
  if (!value) return null;
  const parsed = parseOrderingTimeOfDay(value);
  if (!parsed) return null;
  return `${String(parsed.hours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')}:00`;
}

function getOrderingTimeParts(
  value: string | null | undefined,
  fallbackHours: number,
  fallbackMinutes: number,
): { hours: number; minutes: number } {
  return parseOrderingTimeOfDay(value) ?? {
    hours: fallbackHours,
    minutes: fallbackMinutes,
  };
}

export function awstFromUtc(date: Date): Date {
  const dateInAwst = toZonedTime(date, australiaPerthTz);
  return dateInAwst
}

export function awstToUtc(date: Date): Date {
  const dateInUtc = fromZonedTime(date, australiaPerthTz);
  return dateInUtc
}

export function formatTime(date: Date): string {
  return format(date, 'h:mm a'); // 12-hour format with AM/PM
}

export function formatDate(date: Date): string {
  return format(date, 'eee, d MMM yyyy h:mm a');
}

export function formatDateForReport(date: Date, timePeriod: number): string {
  switch (timePeriod) {
    case 1: return format(date, 'eee, d MMM yyyy')
    case 2: return format(startOfWeek(date, { weekStartsOn: 1 }), 'eee, d MMM yyyy')
    case 3: return format(startOfMonth(date), 'MMM yyyy')
    default: return formatDate(date)
  }
}

export function isInActiveWindow(
  date: Date,
  orderingOpenTime?: string | null,
  orderingCloseTime?: string | null,
): boolean {
  const dateInAwst = awstFromUtc(date);
  const start = earliestPickupTime(dateInAwst, orderingOpenTime)
  const end = latestPickupTime(dateInAwst, orderingCloseTime)
  return isWithinInterval(dateInAwst, { start, end })
}

export function areCustomerOrderingHoursActive(
  orderingOpenTime?: string | null,
  orderingCloseTime?: string | null,
): boolean {
  const now = new Date()
  return isInActiveWindow(now, orderingOpenTime, orderingCloseTime)
}

export function isPickupTimeWithinActiveWindow(
  pickupTime: Date,
  orderingOpenTime?: string | null,
  orderingCloseTime?: string | null,
): boolean {
  return isInActiveWindow(pickupTime, orderingOpenTime, orderingCloseTime)
}

export function earliestPickupTime(date: Date, orderingOpenTime?: string | null): Date {
    const dateInAwst = awstFromUtc(date);
    const start = new Date(dateInAwst)
    const { hours, minutes } = getOrderingTimeParts(orderingOpenTime, 4, 30);
    start.setHours(hours, minutes, 0, 0)
    return start
}

export function latestPickupTime(date: Date, orderingCloseTime?: string | null): Date {
    const dateInAwst = awstFromUtc(date);
    const end = new Date(dateInAwst)
    const { hours, minutes } = getOrderingTimeParts(orderingCloseTime, 18, 0);
    end.setHours(hours, minutes, 0, 0)
    return end
}


export function startOfDayWA(date: Date): Date {
  const dateInAwst = awstFromUtc(date);
  dateInAwst.setHours(0, 0, 0, 0);
  return dateInAwst
}

export function startOfWeekWA(date: Date): Date {
  const dateInAwst = awstFromUtc(date);
  dateInAwst.setHours(0, 0, 0, 0);
  return startOfWeek(dateInAwst, { weekStartsOn: 1 })
}
export function endOfWeekWA(date: Date): Date {
  const dateInAwst = awstFromUtc(date);
  dateInAwst.setHours(0, 0, 0, 0);
  return endOfWeek(dateInAwst, { weekStartsOn: 1 })
}
